/**
 * Hook for managing arche session state persistence
 *
 * Automatically loads session preferences on mount and provides
 * debounced save functionality for settings changes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  api,
  ArcheSessionResponse,
  SingleAIPreferences,
  MultiAIPreferences,
  ImageGeneratorPreferences,
} from '../api';
import { logDebug } from '../telemetry';

export interface UseArcheSessionOptions {
  /** Arche ID to load session for */
  archeId: string;
  /** Whether to auto-load session on mount (default: true) */
  autoLoad?: boolean;
  /** Debounce delay for saves in ms (default: 500) */
  saveDebounceMs?: number;
}

export interface UseArcheSessionResult {
  /** Current session data */
  session: ArcheSessionResponse | null;
  /** Whether session is loading */
  loading: boolean;
  /** Error if load/save failed */
  error: Error | null;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Update Single-AI preferences */
  updateSingleAIPreferences: (updates: Partial<SingleAIPreferences>) => void;
  /** Update Multi-AI preferences */
  updateMultiAIPreferences: (updates: Partial<MultiAIPreferences>) => void;
  /** Update Image Generator preferences */
  updateImageGeneratorPreferences: (updates: Partial<ImageGeneratorPreferences>) => void;
  /** Force save immediately (bypasses debounce) */
  saveNow: () => Promise<void>;
  /** Reload session from server */
  reload: () => Promise<void>;
  /** Reset session (delete from server) */
  reset: () => Promise<void>;
}

/**
 * Hook for managing arche session preferences
 */
export function useArcheSession(options: UseArcheSessionOptions): UseArcheSessionResult {
  const { archeId, autoLoad = true, saveDebounceMs = 500 } = options;

  const [session, setSession] = useState<ArcheSessionResponse | null>(null);
  // Start with loading=true if autoLoad is enabled to prevent flash of empty state
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState<Error | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Track pending updates for debouncing
  const pendingUpdatesRef = useRef<
    Partial<SingleAIPreferences> | Partial<MultiAIPreferences> | Partial<ImageGeneratorPreferences>
  >({});
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Ref to avoid session in update callback deps (prevents infinite save loops)
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Cleanup on unmount - flush any pending saves
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Flush pending saves before unmounting
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save any pending updates synchronously (fire and forget)
      if (Object.keys(pendingUpdatesRef.current).length > 0 && archeId) {
        const toSave = pendingUpdatesRef.current;
        pendingUpdatesRef.current = {};
        api.updateArcheSession(archeId, toSave).catch(() => {
          // Ignore errors on unmount
        });
      }
    };
  }, [archeId]);

  // Load session
  const loadSession = useCallback(async () => {
    if (!archeId) return;

    logDebug('ArcheSession', 'Loading session', { archeId });
    setLoading(true);
    setError(null);

    try {
      const data = await api.getArcheSession(archeId);
      logDebug('ArcheSession', 'Session loaded', {
        archeId,
        activeConversationId:
          'activeConversationId' in (data?.preferences || {})
            ? (data?.preferences as { activeConversationId?: string })?.activeConversationId
            : undefined,
      });
      if (isMountedRef.current) {
        setSession(data);
      }
    } catch (err) {
      logDebug('ArcheSession', 'Load error', { archeId, error: String(err) });
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [archeId]);

  // Save session
  const saveSession = useCallback(
    async (
      updates:
        | Partial<SingleAIPreferences>
        | Partial<MultiAIPreferences>
        | Partial<ImageGeneratorPreferences>,
    ) => {
      if (!archeId) return;

      logDebug('ArcheSession', 'Saving session', { archeId, updates });

      try {
        const data = await api.updateArcheSession(archeId, updates);
        logDebug('ArcheSession', 'Session saved', {
          archeId,
          activeConversationId:
            'activeConversationId' in (data?.preferences || {})
              ? (data?.preferences as { activeConversationId?: string })?.activeConversationId
              : undefined,
        });
        if (isMountedRef.current) {
          setSession(data);
          setIsDirty(false);
        }
      } catch (err) {
        logDebug('ArcheSession', 'Save error', { archeId, error: String(err) });
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    },
    [archeId],
  );

  // Debounced save
  const debouncedSave = useCallback(
    (
      updates:
        | Partial<SingleAIPreferences>
        | Partial<MultiAIPreferences>
        | Partial<ImageGeneratorPreferences>,
    ) => {
      // Merge with pending updates
      pendingUpdatesRef.current = {
        ...pendingUpdatesRef.current,
        ...updates,
      };

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout
      saveTimeoutRef.current = setTimeout(() => {
        const toSave = pendingUpdatesRef.current;
        pendingUpdatesRef.current = {};
        saveSession(toSave);
      }, saveDebounceMs);
    },
    [saveSession, saveDebounceMs],
  );

  // Update Single-AI preferences
  const updateSingleAIPreferences = useCallback(
    (updates: Partial<SingleAIPreferences>) => {
      // Update local state if session exists and is correct type
      if (sessionRef.current?.type === 'single-ai') {
        setSession((prev) => {
          if (!prev || prev.type !== 'single-ai') return prev;
          return {
            ...prev,
            preferences: {
              ...prev.preferences,
              ...updates,
              providerSettings: {
                ...prev.preferences.providerSettings,
                // Deep merge each provider's settings
                openai: {
                  ...prev.preferences.providerSettings?.openai,
                  ...updates.providerSettings?.openai,
                },
                anthropic: {
                  ...prev.preferences.providerSettings?.anthropic,
                  ...updates.providerSettings?.anthropic,
                },
                google: {
                  ...prev.preferences.providerSettings?.google,
                  ...updates.providerSettings?.google,
                },
              },
            },
          };
        });
      }

      // Always save to backend (backend will create session if needed)
      setIsDirty(true);
      debouncedSave(updates);
    },
    [debouncedSave],
  );

  // Update Multi-AI preferences
  const updateMultiAIPreferences = useCallback(
    (updates: Partial<MultiAIPreferences>) => {
      // Update local state if session exists and is correct type
      if (sessionRef.current?.type === 'multi-ai') {
        setSession((prev) => {
          if (!prev || prev.type !== 'multi-ai') return prev;
          return {
            ...prev,
            preferences: {
              ...prev.preferences,
              ...updates,
            },
          };
        });
      }

      // Always save to backend (backend will create session if needed)
      setIsDirty(true);
      debouncedSave(updates);
    },
    [debouncedSave],
  );

  // Update Image Generator preferences
  const updateImageGeneratorPreferences = useCallback(
    (updates: Partial<ImageGeneratorPreferences>) => {
      if (sessionRef.current?.type === 'image-generator') {
        setSession((prev) => {
          if (!prev || prev.type !== 'image-generator') return prev;
          return {
            ...prev,
            preferences: {
              ...prev.preferences,
              ...updates,
              imageSettings: {
                ...prev.preferences.imageSettings,
                ...updates.imageSettings,
              },
            },
          };
        });
      }

      setIsDirty(true);
      debouncedSave(updates);
    },
    [debouncedSave],
  );

  // Force save immediately
  const saveNow = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (Object.keys(pendingUpdatesRef.current).length > 0) {
      const toSave = pendingUpdatesRef.current;
      pendingUpdatesRef.current = {};
      await saveSession(toSave);
    }
  }, [saveSession]);

  // Reload session
  const reload = useCallback(async () => {
    await loadSession();
  }, [loadSession]);

  // Reset session
  const reset = useCallback(async () => {
    if (!archeId) return;

    try {
      await api.deleteArcheSession(archeId);
      if (isMountedRef.current) {
        setSession(null);
        setIsDirty(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [archeId]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      loadSession();
    }
  }, [autoLoad, loadSession]);

  return {
    session,
    loading,
    error,
    isDirty,
    updateSingleAIPreferences,
    updateMultiAIPreferences,
    updateImageGeneratorPreferences,
    saveNow,
    reload,
    reset,
  };
}

export default useArcheSession;
